import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Trash2, Mail, MailOpen, Filter } from "lucide-react";
import { useNotifications } from "@/shared/contexts/NotificationContext.new";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Badge } from "@/shared/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { notifications, markAsRead, markAllAsRead, removeNotification } = useNotifications();
  const [filter, setFilter] = useState<"all" | "unread" | "read">("unread");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const filteredNotifications = notifications.filter((notif) => {
    // Apply read/unread filter
    if (filter === "unread" && notif.read) return false;
    if (filter === "read" && !notif.read) return false;

    // Apply type filter
    if (typeFilter !== "all" && notif.type !== typeFilter) return false;

    return true;
  });

  // Debug: Log all notifications to check action_url
  console.log('[NotificationsPage] Current notifications:', notifications.map(n => ({
    type: n.type,
    title: n.title,
    action_url: n.action_url,
    read: n.read
  })));

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "call-reminder":
        return "📞";
      case "recording-submitted":
      case "recording_received":
        return "🎥";
      case "intake_submitted":
        return "📥";
      case "support_response":
      case "support-message":
        return "💬";
      case "trial-reminder":
      case "trial-expiring":
        return "⏰";
      default:
        return "🔔";
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "call-reminder":
        return "bg-blue-100 dark:bg-blue-900";
      case "recording-submitted":
      case "recording_received":
        return "bg-green-100 dark:bg-green-900";
      case "intake_submitted":
        return "bg-indigo-100 dark:bg-indigo-900";
      case "support_response":
      case "support-message":
        return "bg-purple-100 dark:bg-purple-900";
      case "trial-reminder":
      case "trial-expiring":
        return "bg-amber-100 dark:bg-amber-900";
      default:
        return "bg-gray-100 dark:bg-gray-800";
    }
  };

  const handleNotificationClick = async (notification: typeof notifications[0]) => {
    console.log('[NotificationsPage] Notification clicked:', {
      id: notification.id,
      type: notification.type,
      action_url: notification.action_url,
      title: notification.title
    });

    // Mark as read when clicked
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Recording + intake notifications navigate to Calls with the right tab
    if (
      notification.type === 'recording-submitted' ||
      notification.type === 'recording_received' ||
      notification.type === 'intake_submitted'
    ) {
      // Navigate to calls page with the recording
      if (notification.action_url) {
        console.log('[NotificationsPage] Navigating to recording:', notification.action_url);
        navigate(notification.action_url);
      } else {
        // Fallback to calls page
        console.log('[NotificationsPage] Fallback to /calls');
        navigate('/calls');
      }
      return;
    }

    // Other notifications use navigation
    if (notification.action_url) {
      console.log('[NotificationsPage] Navigating to:', notification.action_url);
      navigate(notification.action_url);
    } else {
      console.log('[NotificationsPage] No action_url for notification type:', notification.type);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 md:h-8 md:w-8" />
            Notifications
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {unreadCount > 0 ? (
              <>
                You have <span className="font-semibold">{unreadCount}</span> unread notification{unreadCount !== 1 && "s"}
              </>
            ) : (
              "You're all caught up!"
            )}
          </p>
        </div>

        {notifications.length > 0 && unreadCount > 0 && (
          <Button
            onClick={markAllAsRead}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <CheckCheck className="h-4 w-4" />
            Mark all read
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Select value={filter} onValueChange={(value: any) => setFilter(value)}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Notifications</SelectItem>
            <SelectItem value="unread">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Unread
              </div>
            </SelectItem>
            <SelectItem value="read">
              <div className="flex items-center gap-2">
                <MailOpen className="h-4 w-4" />
                Read
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="call-reminder">Call Reminders</SelectItem>
            <SelectItem value="recording-submitted">Recordings</SelectItem>
            <SelectItem value="intake_submitted">Intake Requests</SelectItem>
            <SelectItem value="support-message">Support</SelectItem>
            <SelectItem value="trial-reminder">Trial</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>

        {filteredNotifications.length > 0 && (
          <Badge variant="secondary" className="ml-auto self-start">
            {filteredNotifications.length} {filteredNotifications.length === 1 ? "notification" : "notifications"}
          </Badge>
        )}
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length === 0 ? (
          <Card className="p-12 text-center">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No notifications</h3>
            <p className="text-sm text-muted-foreground">
              {filter === "unread"
                ? "You have no unread notifications"
                : filter === "read"
                ? "You have no read notifications"
                : typeFilter !== "all"
                ? `No ${typeFilter} notifications found`
                : "You don't have any notifications yet"}
            </p>
          </Card>
        ) : (
          filteredNotifications.map((notification) => (
            <Card
              key={notification.id}
              className={cn(
                "p-4 cursor-pointer transition-all hover:shadow-md",
                !notification.read && "border-l-4 border-l-primary bg-accent/5"
              )}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-lg",
                    getNotificationColor(notification.type)
                  )}
                >
                  {getNotificationIcon(notification.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4
                      className={cn(
                        "text-sm font-semibold",
                        !notification.read && "text-primary"
                      )}
                    >
                      {notification.title}
                    </h4>

                    {!notification.read && (
                      <Badge variant="default" className="text-xs">
                        New
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">
                    {notification.message}
                  </p>

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                      })}
                    </span>

                    <div className="flex items-center gap-1">
                      {!notification.read && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Mark read
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Empty state for all notifications */}
      {notifications.length === 0 && (
        <Card className="p-12 text-center">
          <Bell className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-30" />
          <h3 className="text-xl font-semibold mb-2">All quiet here</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            When you receive notifications about calls, recordings, or important updates,
            they'll appear here.
          </p>
        </Card>
      )}
    </div>
  );
}
